import { useMemo } from "react";
import PropTypes from "prop-types";
import { FrameWithStickyTitle } from "components/BrandSlides/BrandSlides";
import { getShuffledIcons } from "utils/iconUtils";
import { ensureArray, safeMap, ensureString } from "utils/safeArray";

/**
 * Layout-only grid for AI Ideation tables.
 * - Preserves all provided content verbatim
 * - Adds responsive grid, table semantics, sticky header, and visual polish
 */
export default function AIIdeation({ title, headers, rows, accent_color }) {
  const safeHeaders = ensureArray(headers);
  const safeRows = ensureArray(rows);
  const numCols = safeHeaders.length || 3;

  // Desktop column templates (md and up)
  const gridColsMd =
    numCols === 2
      ? "md:grid-cols-[3fr_5fr]"
      : numCols === 3
      ? "md:grid-cols-[3fr_5fr_5fr]"
      : "md:grid-cols-[3fr_5fr_5fr_5fr]";

  // Icons are shuffled once; fall back to first icon if pool is smaller than rows
  const shuffledIcons = useMemo(() => getShuffledIcons(), []);
  const getIconAt = (i) => shuffledIcons[i % Math.max(shuffledIcons.length, 1)];

  return (
    <FrameWithStickyTitle title={title} accentColor={accent_color}>
      <div className="flex h-full flex-col">
        <div
          role="table"
          aria-label={title}
          className="w-full text-left"
          data-testid="ai-ideation-grid"
        >
          {/* Header */}
          <div
            role="rowgroup"
            className={`grid grid-cols-1 ${gridColsMd} items-end gap-x-5 sticky top-0 z-[1]`}
          >
            {safeMap(safeHeaders, (header, i) => (
              <div
                role="columnheader"
                key={i}
                className={[
                  "border-b",
                  "pb-2 md:pb-3.5",
                  "text-xs md:text-sm font-semibold tracking-wide uppercase",
                  i === 0
                    ? "border-slate-400 text-slate-700"
                    : "border-slate-300 text-slate-500",
                  // Solid background to ensure sticky header sits above rows
                  "bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/75",
                  // On mobile, hide non-first headers
                  i > 0 ? "hidden md:block" : ""
                ].join(" ")}
              >
                {ensureString(header)}
              </div>
            ))}
          </div>

          {/* Body */}
          <div role="rowgroup">
            {safeMap(safeRows, (row, i) => {
              const IconComponent = getIconAt(i);
              const safeRowCells = ensureArray(row);
              return (
                <div
                  role="row"
                  key={i}
                  className={[
                    "grid grid-cols-1",
                    gridColsMd,
                    "gap-x-5",
                    "border-b border-slate-200",
                    "py-2 md:py-3",
                    "items-start",
                    "even:bg-slate-50/35",
                    "hover:bg-slate-50 focus-within:bg-slate-50 transition-colors rounded-xl"
                  ].join(" ")}
                >
                  {safeMap(safeRowCells, (cell, j) => (
                    <div
                      role="cell"
                      key={j}
                      className={[
                        "text-xs md:text-sm leading-relaxed",
                        j === 0
                          ? "font-semibold text-slate-800 bg-slate-50/80 md:bg-transparent rounded-lg md:rounded-none"
                          : "text-slate-700",
                        "flex",
                        j === 0 ? "items-center md:items-start" : "items-start",
                        j === 0 ? "pr-2" : "",
                        // On mobile, add some margin for non-first columns
                        j > 0 ? "mt-1 md:mt-0" : ""
                      ].join(" ")}
                    >
                      {/* Icon only for first column */}
                      {j === 0 && (
                        <div className="mr-2 md:mr-3 ml-2 md:ml-0 flex h-5 w-5 md:h-7 md:w-7 items-center justify-center flex-shrink-0">
                          {IconComponent ? (
                            <IconComponent
                              className="h-4 w-4 md:h-6 md:w-6"
                              style={{ color: accent_color || "#cc0000" }}
                              aria-hidden
                            />
                          ) : null}
                        </div>
                      )}
                      {/* Cell content */}
                      <div
                        className={[
                          j === 0 ? "px-0 md:px-0" : "",
                          // Prevent first-column overflow on small screens
                          j === 0 ? "line-clamp-4 md:line-clamp-none" : ""
                        ].join(" ")}
                      >
                        {ensureString(cell)}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Print styles: flatten effects */}
      <style>{`
        @media print {
          .hover\\:bg-slate-50:hover { background: transparent !important; }
          .even\\:bg-slate-50\\/40:nth-child(even) { background: transparent !important; }
          .sticky { position: static !important; }
        }
      `}</style>
    </FrameWithStickyTitle>
  );
}

AIIdeation.propTypes = {
  title: PropTypes.string,
  headers: PropTypes.array,
  rows: PropTypes.array,
  accent_color: PropTypes.string
};
